import viewpaths from "global/constants/ViewPathConstants";

/**
 * Routes whose query string holds a credential: the guest signing and decline
 * screens read the signer's access token from `?id=`, and /sso/callback lands
 * with the IdP code/state.
 *
 * Analytics has to stay off on these because every Mixpanel event carries
 * `$current_url` — `window.location.href` verbatim, query string included —
 * and nothing redacts it. `record_mask_*` govern only the session-replay
 * recorder, and the `pageview` granularity option decides *when* a pageview
 * fires, not what URL is attached to it.
 *
 * These live here rather than in `mixpanel.ts` because the policy is not
 * Mixpanel-specific: App gates Clarity on the same list, and keeping it free
 * of SDK imports keeps it unit-testable.
 *
 * All three must stay literal path prefixes. If one ever gains a `:param`
 * segment, `startsWith` silently stops matching and the guard no-ops — hence
 * the test alongside this file.
 */
export const CREDENTIAL_BEARING_PATHS = [
  viewpaths.ssoCallbackViewPath,
  viewpaths.doSign,
  viewpaths.declineRequest,
  // CloudStorageOAuthCallback is mounted here and reads the provider's OAuth
  // `code` from the query string. The route is a literal in App's route table
  // rather than a viewpaths constant, so it is spelled out to match.
  "/requests/add",
];

export const isCredentialBearingPath = (pathname: string): boolean =>
  CREDENTIAL_BEARING_PATHS.some((path) => pathname.startsWith(path));

/**
 * Whether the URL is carrying a credential *right now*.
 *
 * The credential always lives in the query string, so one of these paths with
 * an empty query has nothing left to leak. That distinction matters on the SSO
 * callback: SsoCallback scrubs the code via replaceState and only then calls
 * establishSession, but the pathname stays /sso/callback until the router
 * navigates away. Gating on the path alone would therefore block init for the
 * whole login dispatch, and identifyMixpanel — a one-shot call off loginAction
 * with no retry — would silently no-op, leaving every SSO user unidentified.
 */
export const isCredentialBearingUrl = (
  pathname: string,
  search: string,
): boolean =>
  isCredentialBearingPath(pathname) && search.replace(/^\?/, "").length > 0;

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Mixpanel's `block_url_regexes` are tested against the full href, not the
 * pathname, so each pattern is anchored past the scheme and host. Without the
 * anchor `/decline/request` would also match `/foo/decline/request`; over-
 * blocking only costs analytics rather than leaking, but the anchor keeps the
 * list honest about what it covers.
 */
export const CREDENTIAL_BEARING_URL_REGEXES = CREDENTIAL_BEARING_PATHS.map(
  (path) => new RegExp("^https?://[^/]+" + escapeRegExp(path)),
);
